import { useState } from 'react'
import { Link, useParams } from 'react-router'
import { api, useApi, ApiError, type RecDetail } from '../api'
import { useIsMobile } from '../layout'
import { usePageTitle } from '../title'
import { clipLength, usePlayer } from '../player'
import {
  daysSince, dirOf, fmtDate, fmtDateLong, fmtPct, fmtPrice, isStale, kindOf, signClass, toTargetPct,
} from '../format'
import { Chart, indexAtOrAfter, sliceTimeframe, TimeframeTabs, type Timeframe } from '../components/Chart'
import { AiNote, AiTag } from '../components/Disclosures'
import { Conv, DirPill, ErrorState, Icon, Loading, MetricText, NotFound, PodAv } from '../components/primitives'

type Detail = RecDetail['recommendation']

function useClip(r: Detail | undefined) {
  const player = usePlayer()
  if (!r) return null
  const key = `rec:${r.id}`
  const active = player.key === key
  return {
    playing: active && player.playing,
    error: active ? player.error : null,
    length: clipLength(r.timestamp_seconds, r.clip_end_seconds),
    toggle: () =>
      player.play({ key, src: r.episode_audio_url, start: r.timestamp_seconds, end: r.clip_end_seconds }),
  }
}

function PriceHeader({ prices, mobile }: { prices: RecDetail['prices']; mobile: boolean }) {
  const last = prices.at(-1)
  const prev = prices.at(-2)
  const day = last && prev ? (last.close / prev.close - 1) * 100 : null
  const stale = last ? isStale(last.ts) : false
  return (
    <div className="row ai-b g-8" style={{ margin: mobile ? '2px 0 8px' : '2px 0 0', flexWrap: 'wrap' }}>
      <span className="t-num" style={{ fontSize: mobile ? 24 : 22, fontWeight: 600 }}>{fmtPrice(last?.close)}</span>
      {stale && last ? (
        <span className="t-sm" style={{ color: 'var(--ink-3)' }} data-testid="stale">last priced {fmtDate(last.ts)}</span>
      ) : (
        day != null && (
          <span className={`t-num text-${signClass(day) || 'up'}`} style={{ fontSize: 13 }}>
            {fmtPct(day, 2)} last close
          </span>
        )
      )}
    </div>
  )
}

function useSeries(data: RecDetail | undefined, tf: Timeframe) {
  const points = sliceTimeframe(data?.prices ?? [], tf)
  const dates = points.map((p) => p.ts)
  const r = data?.recommendation
  const at = r ? indexAtOrAfter(dates, r.entry_ts) : -1
  return {
    values: points.map((p) => p.close),
    dates,
    markers: at >= 0 && r ? [{ index: at, kind: kindOf(r.type), label: 'REC' }] : [],
  }
}

export function Rec() {
  const { id = '' } = useParams()
  const mobile = useIsMobile()
  const { data, error, retry } = useApi<RecDetail>(api.rec(id))
  const r = data?.recommendation
  usePageTitle(r ? `${r.ticker} · ${dirOf(r.type) ?? 'Mention'}` : undefined)
  const clip = useClip(r)
  const [tf, setTf] = useState<Timeframe>('1M')
  const series = useSeries(data, tf)

  if (error instanceof ApiError && error.status === 404) {
    return <NotFound what="Recommendation"><Link to="/">Back to recommendations</Link></NotFound>
  }
  if (error) return <ErrorState error={error} retry={retry} />
  if (!data || !r || !clip) return <Loading />

  const toTarget = toTargetPct(r.price_target, r.last_price)
  const held = daysSince(r.episode_pub_date)
  const recLine = `Recommended ${fmtDateLong(r.episode_pub_date)} · ${held === 1 ? 'held 1 day' : `held ${held} days`}`
  const episodeLink = (
    <Link to={`/episodes/${r.episode_id}`} style={{ color: 'inherit' }}>
      {r.episode_title}
    </Link>
  )

  if (mobile) {
    return (
      <div data-testid="rec-page">
        <div className="row ai-c g-8" style={{ marginTop: 4, flexWrap: 'wrap' }}>
          <span className="tick" style={{ fontSize: 28, fontWeight: 700 }}>{r.ticker}</span>
          <DirPill type={r.type} />
          {r.price_target != null && <span className="pill accent">Target {fmtPrice(r.price_target)}</span>}
        </div>
        <div className="serif" style={{ fontSize: 22, marginTop: 2 }}>{r.company_name}</div>
        <p className="m-sub" style={{ margin: '2px 0 16px' }}>{recLine}</p>

        {!r.entry_ts && <p className="note" data-testid="unpriced">Priced from the next session’s open.</p>}
        <div className="m-stats">
          <div className="m-stat">
            <span className="l">Since rec</span>
            <span className="v"><MetricText pct={r.pick_return_pct} usd={r.pick_return_usd} /></span>
          </div>
          <div className="m-stat">
            <span className="l">To target</span>
            <span className="v">{toTarget != null ? fmtPct(toTarget, 0) : '—'}</span>
          </div>
          <div className="m-stat">
            <span className="l">Conviction</span>
            <span className="v">{r.conviction != null ? `${r.conviction}/5` : '—'}</span>
          </div>
        </div>

        <div className="m-card" style={{ marginTop: 12 }}>
          <div className="t-xs">Price</div>
          <PriceHeader prices={data.prices} mobile />
          <div className="m-chart">
            <Chart values={series.values} dates={series.dates} markers={series.markers} height={210} variant="mobile" />
          </div>
          <TimeframeTabs value={tf} onChange={setTf} variant="mobile" />
        </div>

        <div className="m-sec"><h3>Why this pick</h3><AiTag /></div>
        <div className="m-quote">
          <div className="q" data-testid="thesis">{r.thesis}</div>
          <div className="row ai-c g-10 mt-18">
            <PodAv name={r.podcast_name} image={r.podcast_image_url} size="lg sq" />
            <div className="col grow">
              <Link to={`/podcasts/${r.podcast_id}`} style={{ fontSize: 14.5, fontWeight: 500, color: 'var(--ink)', padding: '3px 0', textDecoration: 'none' }}>{r.podcast_name}</Link>
              <span style={{ fontSize: 13, color: 'var(--ink-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {episodeLink} · {r.timestamp_label}
              </span>
            </div>
          </div>
        </div>
        <AiNote />

        <div className="m-sec"><h3>Details</h3></div>
        <div className="m-card" style={{ paddingTop: 2, paddingBottom: 2 }}>
          <div className="m-kv">
            <div><span>Target</span><span className="t-num">{r.price_target != null ? fmtPrice(r.price_target) : '—'}</span></div>
            <div><span>Horizon</span><span className="t-num">{r.horizon ?? '—'}</span></div>
            <div><span>Rec price</span><span className="t-num">{fmtPrice(r.entry_price)}</span></div>
            <div><span>Current</span><span className="t-num">{fmtPrice(r.last_price)}</span></div>
          </div>
        </div>

        <div className="m-list" style={{ marginTop: 12 }}>
          <Link to={`/stocks/${r.ticker}`} className="m-row" data-testid="other-mentions">
            <span className="t-num" style={{ fontSize: 22 }}>{r.other_mention_count}</span>
            <div className="meta">
              <span className="a">Other mentions of {r.ticker}</span>
              <span className="b">Across all tracked podcasts</span>
            </div>
            <span className="chev"><Icon name="chevron" size={16} /></span>
          </Link>
        </div>

        <div className="m-sticky">
          {clip.error && <p className="note warn" style={{ marginBottom: 8, pointerEvents: 'auto' }}>{clip.error}</p>}
          <button className="m-btn primary" onClick={clip.toggle} data-testid="play-clip">
            <Icon name={clip.playing ? 'pause' : 'play'} size={16} />
            {clip.playing ? 'Pause' : 'Play clip'}{clip.length ? ` · ${clip.length}` : ''}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div data-testid="rec-page">
      <div className="crumbs" style={{ marginBottom: 16 }}>
        <Link to="/">Recommendations</Link>
        <span className="sep" aria-hidden>/</span>
        <span className="now">{r.ticker} · {dirOf(r.type) ?? 'Mention'}</span>
      </div>

      <div className="row ai-fs jc-sb g-20" style={{ marginBottom: 8 }}>
        <div>
          <div className="row ai-c g-10" style={{ marginBottom: 4 }}>
            <span className="tick" style={{ fontSize: 30, fontWeight: 700 }}>{r.ticker}</span>
            <DirPill type={r.type} />
            {r.price_target != null && <span className="pill accent">Target {fmtPrice(r.price_target)}</span>}
          </div>
          <h1 className="page-title" style={{ fontSize: 26, fontWeight: 500 }}>{r.company_name}</h1>
          <p className="page-sub" style={{ marginBottom: 0 }}>{recLine}</p>
        </div>
        <div className="col g-8" style={{ alignItems: 'flex-end' }}>
          <button className="btn primary" onClick={clip.toggle} data-testid="play-clip">
            <Icon name={clip.playing ? 'pause' : 'play'} size={14} /> {clip.playing ? 'Pause' : 'Play clip'}
          </button>
          {clip.error && <span className="t-sm" style={{ color: 'var(--accent-ink)' }}>{clip.error}</span>}
        </div>
      </div>

      {!r.entry_ts && <p className="note" style={{ marginTop: 18 }} data-testid="unpriced">Priced from the next session’s open.</p>}
      <div className="metric-grid" data-cols="3" style={{ marginTop: 22 }}>
        <div className="metric-tile">
          <span className="l">Since rec</span>
          <span className="v"><MetricText pct={r.pick_return_pct} usd={r.pick_return_usd} /></span>
        </div>
        <div className="metric-tile">
          <span className="l">To target</span>
          <span className="v">{toTarget != null ? fmtPct(toTarget, 0) : '—'}</span>
        </div>
        <div className="metric-tile">
          <span className="l">Conviction</span>
          <span className="v" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {r.conviction != null ? `${r.conviction}/5` : '—'} <Conv n={r.conviction} />
          </span>
        </div>
      </div>

      <div className="chart-wrap" style={{ marginTop: 18 }}>
        <div className="chart-head">
          <div>
            <div className="t-xs">Price</div>
            <PriceHeader prices={data.prices} mobile={false} />
          </div>
          <TimeframeTabs value={tf} onChange={setTf} variant="desktop" />
        </div>
        <Chart values={series.values} dates={series.dates} markers={series.markers} height={260} variant="desktop" />
      </div>

      <div className="detail-grid" style={{ marginTop: 18 }}>
        <div className="card" style={{ background: 'var(--surface-2)', borderColor: 'transparent' }}>
          <div className="row ai-c g-8" style={{ marginBottom: 12 }}><span className="t-xs">Why this pick</span><AiTag /></div>
          <div className="serif" style={{ fontSize: 20, lineHeight: 1.35, color: 'var(--ink)' }} data-testid="thesis">{r.thesis}</div>
          <div className="row ai-c g-10 mt-18">
            <PodAv name={r.podcast_name} image={r.podcast_image_url} />
            <div className="col grow">
              <Link to={`/podcasts/${r.podcast_id}`} style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)', textDecoration: 'none' }}>{r.podcast_name}</Link>
              <span className="t-sm" style={{ color: 'var(--ink-3)' }}>{episodeLink} · {r.timestamp_label}</span>
            </div>
            <button className="btn ghost" onClick={clip.toggle}>
              <Icon name={clip.playing ? 'pause' : 'play'} size={12} /> {clip.length ?? 'Play'}
            </button>
          </div>
          <AiNote />
        </div>

        <div className="col g-10">
          <div className="card tight">
            <div className="t-xs" style={{ marginBottom: 10 }}>Details</div>
            <div className="col g-8">
              <div className="row jc-sb ai-c"><span className="t-sm">Target</span><span className="t-num">{r.price_target != null ? fmtPrice(r.price_target) : '—'}</span></div>
              <div className="row jc-sb ai-c"><span className="t-sm">Horizon</span><span className="t-num">{r.horizon ?? '—'}</span></div>
              <div className="row jc-sb ai-c"><span className="t-sm">Rec price</span><span className="t-num">{fmtPrice(r.entry_price)}</span></div>
              <div className="row jc-sb ai-c"><span className="t-sm">Current</span><span className="t-num">{fmtPrice(r.last_price)}</span></div>
            </div>
          </div>
          <div className="card tight">
            <div className="t-xs" style={{ marginBottom: 10 }}>Other mentions</div>
            <div className="col g-8">
              <div className="row ai-c g-8">
                <span className="t-num" style={{ fontSize: 22, fontWeight: 600 }}>{r.other_mention_count}</span>
                <span className="t-sm">other on <span style={{ color: 'var(--ink)' }}>{r.ticker}</span></span>
              </div>
              <Link className="btn" style={{ width: '100%', justifyContent: 'center', textDecoration: 'none' }} to={`/stocks/${r.ticker}`} data-testid="other-mentions">
                See all on {r.ticker}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
